import { type CylinderParams, isValidCylinder } from './cylinder-params';
import { enCylinderizeSize } from './cylinder-projection';
import { sourceXFromAngle } from './cylinder-x';
import { curveBoundsFromRatio } from './cylinder-y';
import type { Point, Rect } from './types';

/** 경계 판정에서 부동소수 오차로 넘친 비율을 끝값으로 보는 허용치(정규화 비율 기준). */
const EDGE_EPSILON = 1e-9;

/** 구간 안 위치를 시작 끝과 마지막 끝에서 각각 잰 비율(0~1). 오차를 빼면 둘의 합은 1 이다. */
interface SpanFractions {
  readonly fromStart: number;
  readonly fromEnd: number;
}

/** 투영 영역의 한 열. θ 는 원통 각도(0~π), e 는 타원 높이 비율(0~1)이다. */
interface Column {
  readonly theta: number;
  readonly e: number;
}

/**
 * value 가 [start, start + length] 에서 놓인 위치를 양 끝에서 따로 잰다. 1 − 비율을 뺄셈으로 만들지 않으므로
 * 끝 가까이에서도 정밀도를 잃지 않는다. 구간 밖은 두 비율 중 하나가 허용치보다 더 음수인지로만 판정한다.
 * 한쪽 비율이 1 을 조금 넘는 것은 반대쪽 끝(start + length)의 반올림 오차라 1 로 자른다. 숫자가 아니거나 구간 밖이면 null 이다.
 */
function spanFractions(value: number, start: number, length: number): SpanFractions | null {
  const fromStart = (value - start) / length;
  const fromEnd = (start + length - value) / length;
  // NaN 은 비교가 모두 거짓이라 여기서 함께 걸린다.
  if (!(fromStart >= -EDGE_EPSILON && fromEnd >= -EDGE_EPSILON)) return null;
  return {
    fromStart: Math.min(1, Math.max(0, fromStart)),
    fromEnd: Math.min(1, Math.max(0, fromEnd)),
  };
}

/** 투영 x 가 놓인 열을 구한다. 각도는 더 가까운 끝에서 잰 비율로 구한다. */
function columnAt(x: number, projected: Rect): Column | null {
  const fractions = spanFractions(x, projected.left, projected.width);
  if (fractions === null) return null;
  const { fromStart, fromEnd } = fractions;
  const theta =
    fromStart <= fromEnd
      ? 2 * Math.asin(Math.sqrt(fromStart))
      : Math.PI - 2 * Math.asin(Math.sqrt(fromEnd));
  return { theta, e: 2 * Math.sqrt(fromStart * fromEnd) };
}

/**
 * 원본 가로 위치를 투영 x 로 보낸다. 투영 비율은 c = sin²(πu/2) 다.
 * 오른쪽 절반은 1 − c = sin²(π(1 − u)/2) 를 오른쪽 끝에서 빼서 구한다. 오른쪽 끝은 spanFractions 와 같은 식이다.
 */
function projectedXAt({ fromStart, fromEnd }: SpanFractions, projected: Rect): number {
  if (fromStart <= fromEnd) {
    return projected.left + Math.sin((Math.PI * fromStart) / 2) ** 2 * projected.width;
  }
  const right = projected.left + projected.width;
  return right - Math.sin((Math.PI * fromEnd) / 2) ** 2 * projected.width;
}

function finitePointOrNull(point: Point): Point | null {
  return Number.isFinite(point.x) && Number.isFinite(point.y) ? point : null;
}

/**
 * 투영 영역 위의 캔버스 점을 원본 area 의 캔버스 점으로 되돌린다(마플 `getCylinderWarppedCoord` 대응).
 * 가로는 투영 영역의 연속 폭 기준이고, 세로는 그 열의 위·아래 경계 사이 비율로 정한다.
 * 다음 경우는 null 이다.
 * - 투영 영역 좌우 밖이거나 그 열의 위·아래 경계 밖이다.
 * - 좌표가 숫자가 아니거나 계산이 넘친다.
 * - params 가 validateCylinder 를 통과하지 못한다. degree 가 0 이 아닌 경우도 여기에 든다.
 * 양 끝 열(c = 0·1)은 마플과 달리 원본 area 양 끝을 돌려준다. 마플은 이 열에서 (0, 0) 이나 평행 이동한 값을 돌려준다.
 */
export function warpedToSource(point: Point, params: CylinderParams): Point | null {
  if (!isValidCylinder(params)) return null;
  const column = columnAt(point.x, enCylinderizeSize(params));
  if (column === null) return null;
  const { top, bottom } = curveBoundsFromRatio(column.e, params);
  const vertical = spanFractions(point.y, top, bottom - top);
  if (vertical === null) return null;
  const { area } = params;
  return finitePointOrNull({
    x: sourceXFromAngle(column.theta, area),
    y: area.top + vertical.fromStart * area.height,
  });
}

/**
 * 원본 area 의 캔버스 점을 투영 영역 위의 캔버스 점으로 보낸다.
 * 가로는 θ = π·u 에서 c = sin²(θ/2) 로 정한다. 세로는 만든 투영 x 의 열 경계 사이 비율로 정해,
 * warpedToSource 가 같은 x 에서 보는 열과 어긋나지 않게 한다.
 * area 밖이거나 좌표가 숫자가 아니거나 계산이 넘치거나, params 가 validateCylinder 를 통과하지 못하면 null 이다.
 */
export function sourceToWarped(point: Point, params: CylinderParams): Point | null {
  if (!isValidCylinder(params)) return null;
  const { area } = params;
  const horizontal = spanFractions(point.x, area.left, area.width);
  const vertical = spanFractions(point.y, area.top, area.height);
  if (horizontal === null || vertical === null) return null;
  const projected = enCylinderizeSize(params);
  const x = projectedXAt(horizontal, projected);
  const column = columnAt(x, projected);
  if (column === null) return null;
  const { top, bottom } = curveBoundsFromRatio(column.e, params);
  return finitePointOrNull({ x, y: top + vertical.fromStart * (bottom - top) });
}
